/**
 * Admin API client
 */
import { apiClient } from './client';
import { Organization, User } from '@/types';

export interface AdminCompany extends Organization {
  user_count?: number;
}

export interface AdminUser extends User {
  organization_name?: string;
}

export interface AppConfigItem {
  id: string;
  function: string;
  bool_value: boolean | null;
  add_values: Record<string, unknown> | null;
}

export const adminApi = {
  // Companies
  async getCompanies(): Promise<AdminCompany[]> {
    const response = await apiClient.get<AdminCompany[]>('/admin/companies');
    return response || [];
  },

  async createCompany(data: {
    name: string;
    description?: string;
    is_active?: boolean;
    validate_through_database?: boolean;
  }): Promise<AdminCompany> {
    const response = await apiClient.post<AdminCompany>('/admin/companies', data);
    return response.data;
  },

  async updateCompany(id: string, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    validate_through_database?: boolean;
  }): Promise<AdminCompany> {
    const response = await apiClient.put<AdminCompany>(`/admin/companies/${id}`, data);
    return response.data;
  },

  // Users
  async getUsers(): Promise<AdminUser[]> {
    const response = await apiClient.get<any[]>('/admin/users');
    return (response || []) as AdminUser[];
  },

  async createUser(data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    organization_name: string;
    role?: string;
    is_active?: boolean;
  }): Promise<AdminUser> {
    const response = await apiClient.post<AdminUser>('/admin/users', data);
    return response.data;
  },

  async updateUser(id: string, data: {
    email?: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    is_active?: boolean;
    organization_id?: string;
  }): Promise<AdminUser> {
    const response = await apiClient.put<AdminUser>(`/admin/users/${id}`, data);
    return response;
  },

  async updateUserRole(id: string, role: string): Promise<AdminUser> {
    const response = await apiClient.patch<AdminUser>(`/admin/users/${id}/role`, { role });
    return response.data;
  },

  async toggleUserActive(id: string): Promise<AdminUser> {
    const response = await apiClient.patch<AdminUser>(`/admin/users/${id}/toggle-active`);
    return response.data;
  },

  // App configuration (admin only)
  async getAppConfig(): Promise<AppConfigItem[]> {
    const response = await apiClient.get<AppConfigItem[]>('/admin/app-config');
    return response ?? [];
  },

  async createAppConfig(data: {
    function: string;
    bool_value: boolean;
    add_values?: Record<string, unknown> | null;
  }): Promise<AppConfigItem> {
    return apiClient.post<AppConfigItem>('/admin/app-config', data);
  },

  async updateAppConfig(
    id: string,
    data: { function?: string; bool_value?: boolean | null; add_values?: Record<string, unknown> | null }
  ): Promise<AppConfigItem> {
    return apiClient.put<AppConfigItem>(`/admin/app-config/${id}`, data);
  },

  async deleteAppConfig(id: string): Promise<void> {
    await apiClient.delete(`/admin/app-config/${id}`);
  },
};
